<script setup>
import { useForm, usePage, router } from '@inertiajs/vue3';
import { ref, watch, onMounted, onUnmounted, computed } from 'vue';
import ImageUpload from '@/Components/ImageUpload.vue';
import { route } from 'ziggy-js';

const props = defineProps({
  setting: Object,
});

const page = usePage();

// Computed setting for template usage - tracks props changes
const setting = computed(() => props.setting);

// Helper to get asset URL
const getAssetUrl = (path) => {
  if (!path) return null;
  // Handle if path is already full URL
  if (path.startsWith('http')) return path;
  return `/storage/${path}`; // Adjust based on your storage link
};

// Form Setup
const form = useForm({
  // Logos
  setting_logo: null,
  setting_logo_alt: props.setting?.setting_logo_alt || '',
  setting_logo_mobile: null,
  setting_logo_mobile_alt: props.setting?.setting_logo_mobile_alt || '',
  setting_favicon: null,
  setting_logo_small: null,
  setting_logo_large: null,
  setting_logo_google_ads: null, // Asumsikan field ini ada di DB walaupun di controller tadi tidak terlihat di rules semua? Ada di index.blade.php row 183
  setting_logo_google_ads_alt: props.setting?.setting_logo_google_ads_alt || '',

  // Identity
  setting_title: props.setting?.setting_title || '',
  setting_alamat: props.setting?.setting_alamat || '',
  setting_url: props.setting?.setting_url || '',
  setting_email: props.setting?.setting_email || '',
  setting_no_telp: props.setting?.setting_no_telp || '',
  setting_no_wa: props.setting?.setting_no_wa || '',
  
  // Social Media
  setting_url_facebook: props.setting?.setting_url_facebook || '',
  setting_url_x: props.setting?.setting_url_x || '',
  setting_url_instagram: props.setting?.setting_url_instagram || '',
  setting_url_linkedin: props.setting?.setting_url_linkedin || '',
  setting_url_tiktok: props.setting?.setting_url_tiktok || '',

  // Content
  setting_opening_text: props.setting?.setting_opening_text || '',
  setting_map: props.setting?.setting_map || '',

  // SEO Meta fields
  home_meta_title: props.setting?.home_meta_title || '',
  home_meta_description: props.setting?.home_meta_description || '',
  home_meta_keyword: props.setting?.home_meta_keyword || '',
  home_og_image: null,
});

// Separate reactive state for SEO fields to handle chars count independently (or use form for them too)
// Controller logic: update specific 'field' and 'value'. 
// But here we might want to submit specific field updates.

// Per-field saving state
const savingFields = ref({});

// Helper wrapper to update single field with loading state
const updateSetting = (field, value, isFile = false) => {
  // Set loading state for this field
  savingFields.value[field] = true;
  
  // Create a temporary form for single field update
  const formData = {
    field: field,
    value: isFile ? null : value,
    _method: 'PUT'
  };
  
  // For file uploads, add the file directly
  if (isFile && value) {
    formData[field] = value;
  }
  
  const tempForm = useForm(formData);

  const updateUrl = '/admin/setting/update';
  
  // Use post() with forceFormData for file uploads
  tempForm.post(updateUrl, {
    preserveScroll: true,
    forceFormData: isFile, // Force FormData for file uploads
    onSuccess: () => {
      savingFields.value[field] = false;
    },
    onError: () => {
        savingFields.value[field] = false;
    },
    onFinish: () => {
        savingFields.value[field] = false;
    }
  });
};

// Helper to handle image upload and auto-fill alt text
const handleImageUpload = (field, file, altField) => {
    // 1. Update the image file
    updateSetting(field, file, true);
    
    // 2. Auto-generate alt text from filename (only update form model, don't save yet)
    // User can modify and save alt text manually with its own save button
    if (file && altField) {
        const filename = file.name.split('.').slice(0, -1).join('.');
        const formattedAlt = filename
            .replace(/[-_]/g, ' ')
            .replace(/\b\w/g, l => l.toUpperCase());
        form[altField] = formattedAlt;
    }
};

// Flash messages watcher removed - spinner provides visual feedback

// CKEditor Handling
let editorInstance = null;
onMounted(() => {
  if (window.CKEDITOR) {
    editorInstance = window.CKEDITOR.replace('opening-text', {
        height: 200,
        removeButtons: '',
        toolbar: [
            { name: 'basicstyles', items: ['Bold', 'Italic', 'Underline'] },
            { name: 'paragraph', items: ['NumberedList', 'BulletedList'] },
            { name: 'links', items: ['Link', 'Unlink'] },
            { name: 'undo', items: ['Undo', 'Redo'] }
        ]
    });
    
    // Sync data when save button clicked (or on change)
  }
});

// Initialize SEO counters on mount
onMounted(() => {
    metaTitleCount.value = form.home_meta_title?.length || 0;
    metaDescCount.value = form.home_meta_description?.length || 0;
    metaKeywordsCount.value = form.home_meta_keyword?.split(',').filter(k => k.trim().length > 0).length || 0;
});

const saveOpeningText = () => {
    if (editorInstance) {
        const data = editorInstance.getData();
        updateSetting('setting_opening_text', data);
    } else {
        updateSetting('setting_opening_text', form.setting_opening_text);
    }
};

const saveMap = () => {
    updateSetting('setting_map', form.setting_map);
};

// SEO Counters
const metaTitleCount = ref(0);
const metaDescCount = ref(0);
const metaKeywordsCount = ref(0);

// Global Save State
const isSaving = ref(false);

// Save all form data at once
const saveAll = async () => {
    isSaving.value = true;
    
    // Sync CKEditor content before saving
    if (editorInstance) {
        form.setting_opening_text = editorInstance.getData();
    }
    
    // Build form data with all fields
    const formData = new FormData();
    
    // Add all text fields
    const textFields = [
        'setting_title', 'setting_alamat', 'setting_url', 'setting_email',
        'setting_no_telp', 'setting_no_wa', 'setting_logo_alt', 'setting_logo_mobile_alt',
        'setting_logo_google_ads_alt', 'setting_url_facebook', 'setting_url_x',
        'setting_url_instagram', 'setting_url_linkedin', 'setting_url_tiktok',
        'setting_opening_text', 'setting_map'
    ];
    
    textFields.forEach(field => {
        if (form[field]) {
            formData.append(field, form[field]);
        }
    });
    
    // Add file fields if they exist
    const fileFields = ['setting_logo', 'setting_logo_mobile', 'setting_favicon', 
                        'setting_logo_small', 'setting_logo_large', 'setting_logo_google_ads'];
    fileFields.forEach(field => {
        if (form[field] instanceof File) {
            formData.append(field, form[field]);
        }
    });
    
    formData.append('_method', 'PUT');
    
    try {
        const response = await fetch('/setting/update', {
            method: 'POST',
            body: formData,
            headers: {
                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
                'Accept': 'application/json'
            }
        });
        
        if (response.ok) {
            if (window.Notyf) {
                new window.Notyf().success('Setting berhasil disimpan');
            }
        } else {
            const errorData = await response.json();
            if (window.Notyf) {
                new window.Notyf().error(errorData.message || 'Gagal menyimpan setting');
            }
        }
    } catch (error) {
        console.error('Save error:', error);
        if (window.Notyf) {
            new window.Notyf().error('Terjadi kesalahan saat menyimpan');
        }
    } finally {
        isSaving.value = false;
    }
};

const updateSeo = (field, value) => {
    updateSetting(field, value);
};

</script>

<template>
    <div class="container-full">
      <section class="content">
        <div class="row">
          
          <!-- Column 1: Logos -->
          <div class="col-xl-6 col-12">
            
            <div class="row">
               <!-- Logo Desktop -->
               <div class="col-md-6">
                 <div class="box">
                   <div class="box-body">
                     <h4 class="box-title text-primary mb-0"><i class="ti-image me-10"></i>Logo Desktop</h4>
                     <hr class="my-5">
                     <ImageUpload 
                        field="setting_logo"
                        :current-url="setting?.setting_logo ? getAssetUrl('logo/' + setting.setting_logo) : null"
                        alt="Logo Desktop"
                        @update:file="(file) => handleImageUpload('setting_logo', file, 'setting_logo_alt')"
                     />
                     <div class="form-group mt-3">
                        <label for="setting_logo_alt">Alt Logo Desktop</label>
                        <input type="text" id="setting_logo_alt" name="setting_logo_alt" class="form-control" 
                            v-model="form.setting_logo_alt" 
                            @blur="updateSetting('setting_logo_alt', form.setting_logo_alt)"
                        >
                     </div>
                   </div>
                 </div>
               </div>

               <!-- Logo Mobile -->
               <div class="col-md-6">
                 <div class="box">
                   <div class="box-body">
                     <h4 class="box-title text-primary mb-0"><i class="ti-image me-10"></i>Logo Mobile</h4>
                     <hr class="my-5">
                     <ImageUpload 
                        field="setting_logo_mobile"
                        :current-url="setting?.setting_logo_mobile ? getAssetUrl('logo/' + setting.setting_logo_mobile) : null"
                        alt="Logo Mobile"
                        @update:file="(file) => handleImageUpload('setting_logo_mobile', file, 'setting_logo_mobile_alt')"
                     />
                     <div class="form-group mt-3">
                        <label for="setting_logo_mobile_alt">Alt Logo Mobile</label>
                        <input type="text" id="setting_logo_mobile_alt" name="setting_logo_mobile_alt" class="form-control" 
                            v-model="form.setting_logo_mobile_alt" 
                            @blur="updateSetting('setting_logo_mobile_alt', form.setting_logo_mobile_alt)"
                        >
                     </div>
                   </div>
                 </div>
               </div>
            </div>

            <!-- Row 2: Favicon & Small -->
            <div class="row">
               <div class="col-md-6">
                 <div class="box">
                   <div class="box-body">
                     <h4 class="box-title text-primary mb-0"><i class="ti-image me-10"></i>Favicon</h4>
                     <hr class="my-5">
                     <ImageUpload 
                        field="setting_favicon"
                        height="100px"
                        :current-url="setting?.setting_favicon ? getAssetUrl('logo/' + setting.setting_favicon) : null"
                        alt="Favicon"
                        @update:file="(file) => updateSetting('setting_favicon', file, true)"
                     />
                   </div>
                 </div>
               </div>
               
               <div class="col-md-6">
                 <div class="box">
                   <div class="box-body">
                     <h4 class="box-title text-primary mb-0"><i class="ti-image me-10"></i>Logo Small</h4>
                     <hr class="my-5">
                     <ImageUpload 
                        field="setting_logo_small"
                        height="100px"
                        :current-url="setting?.setting_logo_small ? getAssetUrl('logo/' + setting.setting_logo_small) : null"
                        alt="Logo Small"
                        @update:file="(file) => updateSetting('setting_logo_small', file, true)"
                     />
                   </div>
                 </div>
               </div>
            </div>

             <!-- Logo Large -->
             <div class="box">
                <div class="box-body">
                    <h4 class="box-title text-primary mb-0"><i class="ti-image me-10"></i>Logo Large</h4>
                    <hr class="my-5">
                    <ImageUpload 
                    field="setting_logo_large"
                    :current-url="setting?.setting_logo_large ? getAssetUrl('logo/' + setting.setting_logo_large) : null"
                    alt="Logo Large"
                    @update:file="(file) => updateSetting('setting_logo_large', file, true)"
                    />
                </div>
            </div>

            <!-- Logo Google Ads -->
            <div class="box">
                <div class="box-body">
                    <h4 class="box-title text-primary mb-0"><i class="fa fa-google me-10"></i>Logo Google Ads</h4>
                    <hr class="my-5">
                    <ImageUpload 
                    field="setting_logo_google_ads"
                    height="150px"
                    :current-url="setting?.setting_logo_google_ads ? getAssetUrl('logo/' + setting.setting_logo_google_ads) : null"
                    alt="Logo Google Ads"
                    @update:file="(file) => handleImageUpload('setting_logo_google_ads', file, 'setting_logo_google_ads_alt')"
                    />
                    <div class="form-group mt-3">
                        <label for="setting_logo_google_ads_alt">Alt Logo Google Ads</label>
                        <input type="text" id="setting_logo_google_ads_alt" name="setting_logo_google_ads_alt" class="form-control" 
                            v-model="form.setting_logo_google_ads_alt" 
                            @blur="updateSetting('setting_logo_google_ads_alt', form.setting_logo_google_ads_alt)"
                        >
                     </div>
                </div>
            </div>

          </div>

          <!-- Column 2: Identity -->
          <div class="col-xl-6 col-12">
             <!-- ... Identity Box (Keep existing) ... -->
             <div class="box">
                <div class="box-body">
                    <h4 class="box-title text-primary mb-0"><i class="ti-info-alt me-10"></i>Identitas Umum</h4>
                    <hr class="my-10">
                    
                    <div class="form-group">
                        <label for="setting_title">Nama</label>
                        <div class="input-group">
                            <input type="text" id="setting_title" name="setting_title" class="form-control" v-model="form.setting_title">
                            <button type="button" class="btn btn-primary" @click="updateSetting('setting_title', form.setting_title)" :disabled="savingFields.setting_title">
                                <i v-if="savingFields.setting_title" class="fa fa-spinner fa-spin"></i>
                                <i v-else class="fa fa-save"></i>
                            </button>
                        </div>
                    </div>
                    
                    <div class="form-group">
                        <label for="setting_alamat">Alamat</label>
                        <div class="input-group">
                            <input type="text" id="setting_alamat" name="setting_alamat" class="form-control" v-model="form.setting_alamat">
                            <button type="button" class="btn btn-primary" @click="updateSetting('setting_alamat', form.setting_alamat)" :disabled="savingFields.setting_alamat">
                                <i v-if="savingFields.setting_alamat" class="fa fa-spinner fa-spin"></i>
                                <i v-else class="fa fa-save"></i>
                            </button>
                        </div>
                    </div>

                    <div class="row">
                        <div class="col-md-6">
                            <div class="form-group">
                                <label for="setting_url">URL Web</label>
                                <div class="input-group">
                                    <input type="url" id="setting_url" name="setting_url" class="form-control" v-model="form.setting_url">
                                    <button type="button" class="btn btn-primary" @click="updateSetting('setting_url', form.setting_url)" :disabled="savingFields.setting_url">
                                        <i v-if="savingFields.setting_url" class="fa fa-spinner fa-spin"></i>
                                        <i v-else class="fa fa-save"></i>
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div class="col-md-6">
                            <div class="form-group">
                                <label for="setting_email">Email</label>
                                <div class="input-group">
                                    <input type="email" id="setting_email" name="setting_email" class="form-control" v-model="form.setting_email">
                                    <button type="button" class="btn btn-primary" @click="updateSetting('setting_email', form.setting_email)" :disabled="savingFields.setting_email">
                                        <i v-if="savingFields.setting_email" class="fa fa-spinner fa-spin"></i>
                                        <i v-else class="fa fa-save"></i>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="row">
                        <div class="col-md-6">
                            <div class="form-group">
                                <label for="setting_no_telp">No Telepon</label>
                                <div class="input-group">
                                    <input type="text" id="setting_no_telp" name="setting_no_telp" class="form-control" v-model="form.setting_no_telp">
                                    <button type="button" class="btn btn-primary" @click="updateSetting('setting_no_telp', form.setting_no_telp)" :disabled="savingFields.setting_no_telp">
                                        <i v-if="savingFields.setting_no_telp" class="fa fa-spinner fa-spin"></i>
                                        <i v-else class="fa fa-save"></i>
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div class="col-md-6">
                            <div class="form-group">
                                <label for="setting_no_wa">WhatsApp</label>
                                <div class="input-group">
                                    <input type="text" id="setting_no_wa" name="setting_no_wa" class="form-control" v-model="form.setting_no_wa">
                                    <button type="button" class="btn btn-primary" @click="updateSetting('setting_no_wa', form.setting_no_wa)" :disabled="savingFields.setting_no_wa">
                                        <i v-if="savingFields.setting_no_wa" class="fa fa-spinner fa-spin"></i>
                                        <i v-else class="fa fa-save"></i>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="form-group">
                        <label for="opening-text">Kalimat Pembuka</label>
                        <textarea id="opening-text" name="setting_opening_text" class="form-control" rows="5" v-model="form.setting_opening_text"></textarea>
                        <div class="mt-2 text-end">
                             <button type="button" @click="saveOpeningText" class="btn btn-primary">Simpan Kalimat Pembuka</button>
                        </div>
                    </div>

                </div>
             </div>

             <!-- SEO Meta Homepage -->
            <div class="box">
                <div class="box-body">
                    <h4 class="box-title text-primary mb-0"><i class="ti-search me-10"></i>SEO Meta Homepage</h4>
                    <hr class="my-10">

                    <div class="form-group">
                        <label for="home_meta_title">Meta Title Homepage</label>
                        <div class="input-group">
                            <input type="text" id="home_meta_title" name="home_meta_title" class="form-control" 
                                v-model="form.home_meta_title" 
                                maxlength="55"
                                placeholder="Judul untuk homepage (50-55 karakter)"
                                @input="metaTitleCount = $event.target.value.length"
                            >
                            <button type="button" class="btn btn-primary" @click="updateSetting('home_meta_title', form.home_meta_title)" :disabled="savingFields.home_meta_title">
                                <i v-if="savingFields.home_meta_title" class="fa fa-spinner fa-spin"></i>
                                <i v-else class="fa fa-save"></i>
                            </button>
                        </div>
                        <div class="d-flex justify-content-between mt-1">
                            <small class="text-muted">Max 55 karakter</small>
                            <small :class="{'text-success': metaTitleCount >= 50 && metaTitleCount <= 55, 'text-warning': metaTitleCount < 50, 'text-danger': metaTitleCount > 55}">
                                {{ metaTitleCount }}/55
                            </small>
                        </div>
                    </div>

                    <div class="form-group">
                        <label for="home_meta_description">Meta Description Homepage</label>
                        <textarea id="home_meta_description" name="home_meta_description" class="form-control" rows="3"
                            v-model="form.home_meta_description"
                            maxlength="155"
                            placeholder="Deskripsi meta homepage (70-155 karakter)"
                            @input="metaDescCount = $event.target.value.length"
                        ></textarea>
                        <div class="d-flex justify-content-between align-items-center mt-1">
                            <div>
                                <small class="text-muted">Max 155 karakter</small>
                                <small :class="{'text-success': metaDescCount >= 70 && metaDescCount <= 155, 'text-warning': metaDescCount < 70, 'text-danger': metaDescCount > 155}" class="ms-2">
                                    {{ metaDescCount }}/155
                                </small>
                            </div>
                            <button type="button" class="btn btn-sm btn-primary" @click="updateSetting('home_meta_description', form.home_meta_description)" :disabled="savingFields.home_meta_description">
                                <i v-if="savingFields.home_meta_description" class="fa fa-spinner fa-spin me-1"></i>
                                <i v-else class="fa fa-save me-1"></i>
                                Simpan
                            </button>
                        </div>
                    </div>

                     <div class="form-group">
                        <label for="home_meta_keyword">Meta Keywords Homepage</label>
                         <textarea id="home_meta_keyword" name="home_meta_keyword" class="form-control" rows="3"
                            v-model="form.home_meta_keyword"
                            placeholder="Pisahkan dengan koma"
                            @input="metaKeywordsCount = $event.target.value.split(',').filter(k => k.trim().length > 0).length"
                        ></textarea>
                         <div class="d-flex justify-content-between align-items-center mt-1">
                            <div>
                                <small class="text-muted">Pisahkan dengan koma</small>
                                <small class="text-muted ms-2">
                                    {{ metaKeywordsCount }} keywords
                                </small>
                            </div>
                            <button type="button" class="btn btn-sm btn-primary" @click="updateSetting('home_meta_keyword', form.home_meta_keyword)" :disabled="savingFields.home_meta_keyword">
                                <i v-if="savingFields.home_meta_keyword" class="fa fa-spinner fa-spin me-1"></i>
                                <i v-else class="fa fa-save me-1"></i>
                                Simpan
                            </button>
                        </div>
                    </div>

                </div>
            </div>
            
            <!-- Social Share Image -->
             <div class="box">
                <div class="box-body">
                    <h4 class="box-title text-primary mb-0"><i class="ti-share me-10"></i>Social Media Share Image</h4>
                    <hr class="my-10">
                    <div class="form-group">

                         <ImageUpload 
                        field="home_og_image"
                        label="Social Share Image"
                        height="200px"
                        :current-url="setting?.home_og_image ? getAssetUrl('dashboard/' + setting.home_og_image) : null"
                        alt="Social Share Image"
                        @update:file="(file) => updateSetting('home_og_image', file, true)"
                        />
                         <small class="text-muted mt-2 d-block">
                            <i class="fa fa-info-circle me-5"></i>
                            Gambar ini akan digunakan untuk preview saat website dibagikan di social media.
                        </small>
                    </div>
                </div>
            </div>
             
             <!-- Social Media Links (Using previous implementation) -->
             <div class="box">
                <div class="box-body">
                    <h4 class="box-title text-primary mb-0"><i class="ti-share me-10"></i>Sosial Media</h4>
                    <hr class="my-10">
                    
                    <div class="form-group">
                        <label for="setting_url_facebook">Facebook</label>
                        <div class="input-group">
                            <input type="url" id="setting_url_facebook" name="setting_url_facebook" class="form-control" v-model="form.setting_url_facebook">
                            <button type="button" class="btn btn-primary" @click="updateSetting('setting_url_facebook', form.setting_url_facebook)" :disabled="savingFields.setting_url_facebook">
                                <i v-if="savingFields.setting_url_facebook" class="fa fa-spinner fa-spin"></i>
                                <i v-else class="fa fa-save"></i>
                            </button>
                        </div>
                    </div>
                    <div class="form-group">
                        <label for="setting_url_x">X (Twitter)</label>
                        <div class="input-group">
                            <input type="url" id="setting_url_x" name="setting_url_x" class="form-control" v-model="form.setting_url_x">
                            <button type="button" class="btn btn-primary" @click="updateSetting('setting_url_x', form.setting_url_x)" :disabled="savingFields.setting_url_x">
                                <i v-if="savingFields.setting_url_x" class="fa fa-spinner fa-spin"></i>
                                <i v-else class="fa fa-save"></i>
                            </button>
                        </div>
                    </div>
                    <div class="form-group">
                        <label for="setting_url_instagram">Instagram</label>
                        <div class="input-group">
                            <input type="url" id="setting_url_instagram" name="setting_url_instagram" class="form-control" v-model="form.setting_url_instagram">
                            <button type="button" class="btn btn-primary" @click="updateSetting('setting_url_instagram', form.setting_url_instagram)" :disabled="savingFields.setting_url_instagram">
                                <i v-if="savingFields.setting_url_instagram" class="fa fa-spinner fa-spin"></i>
                                <i v-else class="fa fa-save"></i>
                            </button>
                        </div>
                    </div>
                    <div class="form-group">
                        <label for="setting_url_linkedin">LinkedIn</label>
                        <div class="input-group">
                            <input type="url" id="setting_url_linkedin" name="setting_url_linkedin" class="form-control" v-model="form.setting_url_linkedin">
                            <button type="button" class="btn btn-primary" @click="updateSetting('setting_url_linkedin', form.setting_url_linkedin)" :disabled="savingFields.setting_url_linkedin">
                                <i v-if="savingFields.setting_url_linkedin" class="fa fa-spinner fa-spin"></i>
                                <i v-else class="fa fa-save"></i>
                            </button>
                        </div>
                    </div>
                    <div class="form-group">
                        <label for="setting_url_tiktok">TikTok</label>
                        <div class="input-group">
                            <input type="url" id="setting_url_tiktok" name="setting_url_tiktok" class="form-control" v-model="form.setting_url_tiktok">
                            <button type="button" class="btn btn-primary" @click="updateSetting('setting_url_tiktok', form.setting_url_tiktok)" :disabled="savingFields.setting_url_tiktok">
                                <i v-if="savingFields.setting_url_tiktok" class="fa fa-spinner fa-spin"></i>
                                <i v-else class="fa fa-save"></i>
                            </button>
                        </div>
                    </div>
                </div>
             </div>

             <!-- Map -->
             <div class="box">
                <div class="box-body">
                    <h4 class="box-title text-primary mb-0"><i class="ti-map-alt me-10"></i>Map</h4>
                    <hr class="my-10">
                     <div class="form-group">
                        <label for="setting_map">Embed Map</label>
                        <textarea id="setting_map" name="setting_map" class="form-control" rows="8" v-model="form.setting_map"></textarea>
                    </div>
                    <div class="mt-2 text-end">
                        <button type="button" @click="saveMap" class="btn btn-primary">Simpan Peta</button>
                    </div>
                </div>
             </div>

          </div>

        </div>
      </section>
    </div>
</template>
